
import { Component, Prop } from 'vue-property-decorator'
import store from '@/store/index'
import vuetify from '@/plugins/vuetify'

import { Mixins } from 'vue-mixin-decorator'
import MixinInterface from '@/_mixins/index'
import ObservationMixin from '@/_mixins/observation'
import CompanyMixin from '@/_mixins/company'

import {
    groupByProject,
    groupByProjectGroup,
    observationStatisticsArrayValues,
    observationStatisticsFilters,
    observationStatisticsObjectValues,
    observationStatisticsSingleValues,
    setObservationFilter,
    statusColor,
    priorityColor,
    severityColor,
    dueStatusColor,
    sortFilters,
} from '@/types/observation'

import WidgetTemplate from '@/views/Project/Dashboard/components/widget.vue'

import LineChart from '@/components/Charts/Line/Line.vue'
import BarChart from '@/components/Charts/Bar/Bar.vue'
import PieChart from '@/components/Charts/Pie.vue'

import ChartTypeFilter from '@/views/Project/Dashboard/filters/chartType.vue'
import AggregateFieldFilter from '@/views/Project/Observation/components/filters/aggregateField.vue'
import AggregateFunctionFilter from '@/views/Project/Observation/components/filters/aggregateFunction.vue'
import AggregatePeriodFilter from '@/views/Project/Observation/components/filters/aggregatePeriod.vue'
import AggregatePeriodSourceFilter from '@/views/Project/Observation/components/filters/aggregatePeriodSource.vue'
import GroupByilter from '@/views/Project/Dashboard/filters/groupBy.vue'
import NumberFormatFilter from '@/views/Project/Dashboard/filters/numberFormat.vue'
import ProjectFilter from '@/views/Project/components/project.vue'
import StatusFilter from '@/views/Project/Observation/components/status.vue'
import PriorityFilter from '@/views/Project/Observation/components/priority.vue'
import SeverityFilter from '@/views/Project/Observation/components/severity.vue'
import DueStatusFilter from '@/views/Project/Observation/components/filters/dueStatus.vue'
import PeriodFilter from '@/views/Project/Observation/components/filters/period.vue'
import TreeitemFilter from '@/views/Project/Observation/components/filters/treeitem.vue'
import TreeitemGroupFilter from '@/views/Project/Observation/components/filters/treeitemGroup.vue'
import ContractorFilter from '@/views/Project/Observation/components/contractorPicker.vue'
import RelatedCompaniesFilter from '@/views/Project/Observation/components/filters/relatedCompanies.vue'
import ReporterIsNullFilter from '@/views/Project/Observation/components/filters/reporterIsNull.vue'
import UsersFilter from '@/views/Project/Observation/components/userAutocomplete.vue'
import { getColorForValue } from '@/utils/colors'

@Component({
    name: 'ObservationStatWidget',
    mixins: [ObservationMixin, CompanyMixin],
    components: {
        WidgetTemplate,
        LineChart,
        BarChart,
        PieChart,
        ChartTypeFilter,
        AggregateFieldFilter,
        AggregateFunctionFilter,
        AggregatePeriodFilter,
        AggregatePeriodSourceFilter,
        GroupByilter,
        NumberFormatFilter,
        ProjectFilter,
        StatusFilter,
        PriorityFilter,
        SeverityFilter,
        DueStatusFilter,
        PeriodFilter,
        TreeitemFilter,
        TreeitemGroupFilter,
        ContractorFilter,
        RelatedCompaniesFilter,
        ReporterIsNullFilter,
        UsersFilter,
    },
})
export default class ObservationStatWidget extends Mixins<MixinInterface>(
    ObservationMixin,
    CompanyMixin
) {
    // PROPS
    @Prop({ default: false }) isStaticWidget!: boolean
    @Prop({ required: true }) component!: any
    @Prop({ required: false }) dashboardId!: string

    // SYSTEM VARIABLES
    store = store
    vuetify: any = vuetify

    url = process.env.VUE_APP_OBSERVATIONS_BASE_URL

    // CHARTJS
    datasets: any = []
    labels: any = {}
    loading = true

    get groupBy() {
        const groupBy = this.project ? groupByProject : groupByProjectGroup
        return groupBy.map((item) => {
            const translationKey = `observation_labels['observation.${item}']`
            let text = this.$te(translationKey)
                ? this.$t(translationKey)
                : this.$t(`observation_fe['filter.groupBy.${item}']`)
            return {
                text: text,
                value: item,
            }
        })
    }

    get categories() {
        return this.store.getters.observationCategories
    }

    get causes() {
        return this.store.getters.observationCauses
    }

    get locations() {
        return this.$store.getters.projectLocations
    }

    // Values
    widgetSettings = null as null | Record<string, any>
    widgetSettingsBase = null as null | Record<string, any>

    setValues() {
        let settings: any = {
            chartType: 'bar',
            numberFormat: null,
            ...this.lodash.cloneDeep(observationStatisticsFilters),
        }

        if (this.widget) {
            Object.keys(settings).forEach((key) => {
                if (
                    this.widget.settings[this.viewType] &&
                    Object.prototype.hasOwnProperty.call(this.widget.settings[this.viewType], key)
                ) {
                    settings[key] = this.widget.settings[this.viewType][key]
                } else if (
                    this.widget.settings.default &&
                    Object.prototype.hasOwnProperty.call(this.widget.settings.default, key)
                ) {
                    settings[key] = this.widget.settings.default[key]
                } else if (Object.prototype.hasOwnProperty.call(this.widget.settings, key)) {
                    settings[key] = this.widget.settings[key]
                }
            })
        }

        this.widgetSettingsBase = this.lodash.cloneDeep(settings)
        this.widgetSettings = this.lodash.cloneDeep(settings)
    }

    widgetOldHeight = null

    get project() {
        let projectId = null as null | string
        if (this.widgetSettings?.project) {
            projectId = this.widgetSettings?.project
        } else if (this.$route.params.type == 'p') {
            projectId = this.$route.params.id
        }
        return projectId ? this.$store.getters.project(projectId) : null
    }

    get group() {
        let groupId = null as null | string
        if (this.widgetSettings?.group) {
            groupId = this.widgetSettings?.group
        } else if (this.$route.params.type == 'g' && !this.project) {
            groupId = this.$route.params.id
        }

        return groupId ? this.$store.getters.group(groupId) : null
    }

    get permissions() {
        let arr: Array<any> = []
        if (this.project) {
            arr = this.project.attributes.permissions
        } else {
            Object.values(this.$store.getters.projects).map((project: any) => {
                arr = [...arr, ...project.attributes.permissions]
            })
        }
        return arr
    }

    get hasAccess() {
        return this.permissions.includes('observation.view_observation_statistics')
    }

    // STORE VARIABLES
    get viewportWidth() {
        return this.vuetify.framework.breakpoint ? this.vuetify.framework.breakpoint.width : null
    }

    // TIME
    get max() {
        return new Date().toISOString().substr(0, 10)
    }

    get timeZone() {
        return this.project ? this.project.attributes.timeZone : 'Europe/Oslo'
    }

    // FILTER BY RELATED COMPANY
    inputRelatedCompany = ''
    relatedCompanies: any = []
    loadingRelatedCompany = false

    // WIDGET SETTINGS
    get widget() {
        // If widget belongs to dashboard than it will be stored in vuex
        // For other modules it is currently passed as a param
        const dashboardWidget = this.store.getters.widgetData(this.component.id)
        return dashboardWidget ? dashboardWidget : this.component
    }

    settingsBase: any = {}

    get placement() {
        if (!this.widget || this.isStaticWidget) return null

        const result = {}

        Object.keys(this.widget.placement.default).forEach((key) => {
            result[key] =
                this.widget.placement[this.device(this.viewportWidth)][key] ||
                typeof this.widget.placement[this.device(this.viewportWidth)][key] == 'boolean'
                    ? this.widget.placement[this.device(this.viewportWidth)][key]
                    : this.widget.placement.default[key]
        })

        return result
    }

    // Settings

    get widgetSettingsUpdates() {
        return this.difference(this.widgetSettings, this.widgetSettingsBase)
    }

    get isSettingsUpdated() {
        return !this.isObjectEmpty(this.widgetSettingsUpdates)
    }

    async updateWidget() {
        // General logic to store widgets for the dashboard
        if (this.dashboardId && !this.isStaticWidget) {
            let widgets = this.lodash.cloneDeep(this.store.getters.widgetsData)
            let widget = widgets[this.component.id]

            widget.settings[this.viewType] = this.lodash.cloneDeep(this.widgetSettings)

            Object.keys(this.widgetSettingsUpdates).forEach((key) => {
                if (key === 'chartType') {
                    let height = this.placement.h
                    if (this.widgetSettingsUpdates[key] === 'pie') {
                        height = Math.ceil(this.widget.placement[this.viewType].w * 1.5) // min height for pie chart
                        this.widgetOldHeight = this.widget.placement[this.viewType].h
                    } else if (this.widgetOldHeight) {
                        height = this.widgetOldHeight
                    }
                    widget.placement[this.viewType].h = height

                    let widgetEl = document.querySelector(`#gsi-${widget.id}`)
                    if (widgetEl) {
                        let gridStackItemEl = widgetEl.closest('.grid-stack-item')
                        gridStackItemEl?.setAttribute('gs-h', height.toString())
                    }
                }
            })

            // Commit the cloned and modified state back to the store
            this.store.commit('widgetsData', widgets)
        }

        // Fetch updated statistics if needed
        await this.getStat()
    }

    mounted() {
        this.setValues()
        this.getStat()
    }

    get observationsParams() {
        return {
            ...(this.group &&
                !this.widgetSettings?.project && {
                    'filter[projectGroup]': this.group.id,
                }),
            ...(this.widgetSettings?.project && {
                'filter[project]': this.widgetSettings?.project,
            }),
            ...(this.widgetSettings?.status &&
                this.widgetSettings?.status.length > 0 && {
                    'filter[status]': this.widgetSettings?.status.join(),
                }),
            ...(this.widgetSettings?.priority &&
                this.widgetSettings?.priority.length > 0 && {
                    'filter[priority]': this.widgetSettings?.priority.join(),
                }),
            ...(this.widgetSettings?.severity &&
                this.widgetSettings?.severity.length > 0 && {
                    'filter[severity]': this.widgetSettings?.severity.join(),
                }),
            ...(this.widgetSettings?.dueStatus &&
                this.widgetSettings?.dueStatus.length > 0 && {
                    'filter[dueStatus]': this.widgetSettings?.dueStatus.join(),
                }),

            ...(!this.group &&
                this.widgetSettings?.category.length > 0 && {
                    'filter[category]': this.widgetSettings?.category.join(),
                }),
            ...(this.group &&
                this.widgetSettings?.categoryName.length > 0 && {
                    'filter[category.name]': this.widgetSettings?.categoryName.join(),
                }),
            ...(!this.group &&
                this.widgetSettings?.cause.length > 0 && {
                    'filter[cause]': this.widgetSettings?.cause.join(),
                }),
            ...(this.group &&
                this.widgetSettings?.causeName.length > 0 && {
                    'filter[cause.name]': this.widgetSettings?.causeName.join(),
                }),
            ...(!this.group &&
                this.widgetSettings?.location.length > 0 && {
                    'filter[location]': this.widgetSettings?.location.join(),
                }),
            ...(this.group &&
                this.widgetSettings?.locationName.length > 0 && {
                    'filter[location.name]': this.widgetSettings?.locationName.join(),
                }),

            ...(this.widgetSettings?.reporter &&
                this.widgetSettings?.reporter.length > 0 && {
                    'filter[reporter]': this.widgetSettings?.reporter.join(),
                }),
            ...((this.widgetSettings?.reporterIsNull == false ||
                this.widgetSettings?.reporterIsNull == true) && {
                'filter[reporter][isNull]': this.widgetSettings?.reporterIsNull,
            }),
            ...(this.widgetSettings?.responsibleUser &&
                this.widgetSettings?.responsibleUser.length > 0 && {
                    'filter[responsibleUser]': this.widgetSettings?.responsibleUser.join(),
                }),
            ...(this.widgetSettings?.responsibleLeader &&
                this.widgetSettings?.responsibleLeader.length > 0 && {
                    'filter[responsibleLeader]': this.widgetSettings?.responsibleLeader.join(),
                }),
            ...(this.widgetSettings?.contractors &&
                this.widgetSettings?.contractors.length > 0 && {
                    'filter[contractors][contains]': this.widgetSettings?.contractors.join(),
                }),
            ...(this.widgetSettings?.relatedCompanies &&
                this.widgetSettings?.relatedCompanies.length > 0 && {
                    'filter[relatedCompanies][contains]':
                        this.widgetSettings?.relatedCompanies.join(),
                }),
            ...(this.widgetSettings?.reporterCompany &&
                this.widgetSettings?.reporterCompany.length > 0 && {
                    'filter[reporterCompany]': this.widgetSettings?.reporterCompany.join(),
                }),

            ...((this.widgetSettings?.happenedAt?.period ||
                this.widgetSettings?.happenedAt?.ge) && {
                'filter[happenedAt][ge]': this.widgetSettings?.happenedAt.period
                    ? this.generalFormat(
                          new Date(
                              Date.now() - 86400 * 1000 * this.widgetSettings?.happenedAt.period
                          ),
                          this.timeZone
                      )
                    : this.widgetSettings?.happenedAt.ge,
            }),
            ...((this.widgetSettings?.happenedAt?.period ||
                this.widgetSettings?.happenedAt?.le) && {
                'filter[happenedAt][le]': this.widgetSettings?.happenedAt.period
                    ? this.generalFormat(new Date(Date.now()), this.timeZone)
                    : this.widgetSettings?.happenedAt.le,
            }),
        }
    }

    get params() {
        return {
            aggregateFunction: this.widgetSettings?.aggregateFunction
                ? this.widgetSettings?.aggregateFunction
                : 'count',
            ...(this.widgetSettings?.aggregateFunction != 'count' && {
                aggregateField: this.widgetSettings?.aggregateField
                    ? this.widgetSettings?.aggregateField
                    : 'timePublishedToClosed',
            }),
            ...(this.widgetSettings?.aggregatePeriod && {
                aggregatePeriod: this.widgetSettings?.aggregatePeriod,
            }),
            ...(this.widgetSettings?.aggregatePeriodSource && {
                aggregatePeriodSource: this.widgetSettings?.aggregatePeriodSource,
            }),
            ...(this.widgetSettings?.groupBy && { groupBy: this.widgetSettings?.groupBy }),

            ...((this.widgetSettings?.publishedAt?.period ||
                this.widgetSettings?.publishedAt?.ge) && {
                'filter[publishedAt][ge]': this.widgetSettings?.publishedAt.period
                    ? this.generalFormat(
                          new Date(
                              Date.now() - 86400 * 1000 * this.widgetSettings?.publishedAt.period
                          ),
                          this.timeZone
                      )
                    : this.widgetSettings?.publishedAt.ge,
            }),
            ...((this.widgetSettings?.publishedAt?.period ||
                this.widgetSettings?.publishedAt?.le) && {
                'filter[publishedAt][le]': this.widgetSettings?.publishedAt.period
                    ? this.generalFormat(new Date(Date.now()), this.timeZone)
                    : this.widgetSettings?.publishedAt.le,
            }),
            ...((this.widgetSettings?.updatedAt?.period || this.widgetSettings?.updatedAt?.ge) && {
                'filter[updatedAt][ge]': this.widgetSettings?.updatedAt.period
                    ? this.generalFormat(
                          new Date(
                              Date.now() - 86400 * 1000 * this.widgetSettings?.updatedAt.period
                          ),
                          this.timeZone
                      )
                    : this.widgetSettings?.updatedAt.ge,
            }),
            ...((this.widgetSettings?.updatedAt?.period || this.widgetSettings?.updatedAt?.le) && {
                'filter[updatedAt][le]': this.widgetSettings?.updatedAt.period
                    ? this.generalFormat(new Date(Date.now()), this.timeZone)
                    : this.widgetSettings?.updatedAt.le,
            }),
            ...((this.widgetSettings?.closedAt?.period || this.widgetSettings?.closedAt?.ge) && {
                'filter[closedAt][ge]': this.widgetSettings?.closedAt.period
                    ? this.generalFormat(
                          new Date(
                              Date.now() - 86400 * 1000 * this.widgetSettings?.closedAt.period
                          ),
                          this.timeZone
                      )
                    : this.widgetSettings?.closedAt.ge,
            }),
            ...((this.widgetSettings?.closedAt?.period || this.widgetSettings?.closedAt?.le) && {
                'filter[closedAt][le]': this.widgetSettings?.closedAt.period
                    ? this.generalFormat(new Date(Date.now()), this.timeZone)
                    : this.widgetSettings?.closedAt.le,
            }),
            ...this.observationsParams,
        }
    }

    async getStat() {
        if (this.widgetSettings) {
            try {
                this.loading = true
                let statsArray = await this.getObservationStatistics(this.params)
                this.datasets = []
                this.labels = {}

                statsArray.map((el: any) => {
                    el.data.map((el: any) => {
                        el.p ? (this.labels[el.p] = el.p) : ''
                    })
                })

                this.labels = Object.values(this.labels).sort((a: any, b: any) => {
                    let dateA = new Date(a).getTime()
                    let dateB = new Date(b).getTime()
                    return dateA > dateB ? 1 : -1
                })

                this.labels.length == 0 ? (this.labels = ['']) : ''

                statsArray.map((item: any, index: number) => {
                    const color = item.groupValue
                        ? getColorForValue(`${item.groupResource?.data?.id}${item.groupValue}`)
                        : this.vuetify.framework.theme.themes.light['primary']

                    let dataset: any = {
                        label: item.groupValue
                            ? item.groupValue
                            : this.$t("observation_fe['filter.groupBy.null']"),
                        data: [],
                        borderColor: color,
                        backgroundColor: color,
                        id: item.groupValue,
                    }

                    if (this.widgetSettings?.groupBy) {
                        const res = this.formatDatasetGroupBy(this.widgetSettings?.groupBy, item)

                        dataset = { ...dataset, ...res }
                    }

                    this.labels.map((date: any) => {
                        let data = item.data.find((item: any) => {
                            return this.widgetSettings?.aggregatePeriod ? item.p == date : true
                        })

                        dataset.data.push({
                            x: this.dateFormat(date),
                            y: data
                                ? this.widgetSettings?.aggregateFunction != 'count'
                                    ? this.durationFormat(data.v, 'seconds')
                                    : data.v
                                : 0,
                            p: this.periodFormat(date),
                        })
                    })

                    this.datasets.push(dataset)
                })

                this.widgetSettings?.aggregatePeriod
                    ? (this.labels = this.labels.map((date: any) => (date = this.dateFormat(date))))
                    : ''
            } finally {
                this.loading = false
            }
        }
    }

    dateFormat(date: string) {
        let label = ''
        let data: any = null
        switch (this.widgetSettings?.aggregatePeriod) {
            case 'day':
                label = this.generalFormat(date, this.timeZone, 'DD.MM.YYYY')
                break
            case 'week':
                data = this.generalFormat(date, this.timeZone, 'WW YYYY')
                label = this.$t("common_timeperiod_fe['aggregatePeriod.label.short.week']", {
                    data: data,
                }).toString()
                break
            case 'month':
                label = this.generalFormat(date, this.timeZone, "MMMM'YY")
                break
            case 'quarter':
                data = this.generalFormat(date, this.timeZone, 'Q YYYY')
                label = this.$t("common_timeperiod_fe['aggregatePeriod.label.short.quarter']", {
                    data: data,
                }).toString()
                break
            case 'year':
                label = this.generalFormat(date, this.timeZone, 'YYYY')
                break
        }
        return label
    }

    periodFormat(date: string) {
        let period = null as null | Record<string, string>

        if (!this.widgetSettings?.aggregatePeriod || !date) {
            return period
        }

        const startDate = new Date(date)

        switch (this.widgetSettings?.aggregatePeriod) {
            case 'day': {
                const endDate = new Date(startDate)
                endDate.setDate(startDate.getDate() + 1)
                period = {
                    ge: startDate.toISOString(),
                    le: endDate.toISOString(),
                }
                break
            }
            case 'week': {
                const endDate = new Date(startDate)
                endDate.setDate(startDate.getDate() + 7)
                period = {
                    ge: startDate.toISOString(),
                    le: endDate.toISOString(),
                }
                break
            }
            case 'month': {
                const endDate = new Date(startDate)
                endDate.setMonth(startDate.getMonth() + 1)
                period = {
                    ge: startDate.toISOString(),
                    le: endDate.toISOString(),
                }
                break
            }
            case 'quarter': {
                const endDate = new Date(startDate)
                endDate.setMonth(startDate.getMonth() + 3)
                period = {
                    ge: startDate.toISOString(),
                    le: endDate.toISOString(),
                }
                break
            }
            case 'year': {
                const endDate = new Date(startDate)
                endDate.setFullYear(startDate.getFullYear() + 1)
                period = {
                    ge: startDate.toISOString(),
                    le: endDate.toISOString(),
                }
                break
            }
        }
        return period
    }

    formatDatasetGroupBy(groupBy: string, item: any) {
        let color = 'grey'
        let label = ''
        let id = null

        switch (groupBy) {
            case 'status':
                id = item.groupValue
                label = this.$t(
                    `observation_enums['observation.status.${item.groupValue}']`
                ).toString()
                color = statusColor[item.groupValue]
                break

            case 'severity':
                if (item.groupValue) {
                    id = item.groupValue
                    label = this.$t(
                        `observation_enums['observation.severity.${item.groupValue}']`
                    ).toString()

                    color = severityColor[item.groupValue]
                } else {
                    label = this.$t(
                        `observation_fe['filter.groupBy.${this.widgetSettings?.groupBy}.default']`
                    ).toString()
                }
                break

            case 'priority':
                if (item.groupValue) {
                    id = item.groupValue
                    label = this.$t(
                        `observation_enums['observation.priority.${item.groupValue}']`
                    ).toString()

                    color = priorityColor[item.groupValue]
                } else {
                    label = this.$t(
                        `observation_fe['filter.groupBy.${this.widgetSettings?.groupBy}.default']`
                    ).toString()
                }
                break

            case 'dueStatus':
                if (item.groupValue) {
                    id = item.groupValue
                    label = this.$t(
                        `observation_enums['observation.dueStatus.${item.groupValue}']`
                    ).toString()

                    color = dueStatusColor[item.groupValue]
                } else {
                    label = this.$t(
                        `observation_fe['filter.groupBy.${this.widgetSettings?.groupBy}.default']`
                    ).toString()
                }
                break
            case 'reporterRole':
            case 'reporterCompanyRole':
                if (item.groupResource?.data?.kind) {
                    if (item.groupResource?.data?.kind == 'normal') {
                        id = item.groupResource.data.id
                        label = `${item.groupResource.data.name}`
                        color = getColorForValue(
                            `${item.groupResource.data?.id}${item.groupResource.data.name}`
                        )
                    } else {
                        id = item.groupResource.data.id
                        label = this.$t(
                            `authorization_enums['role.kind.${item.groupResource.data.kind}']`
                        ).toString()
                        color = getColorForValue(
                            `${item.groupResource.data?.id}${item.groupResource.data.kind}`
                        )
                    }
                } else {
                    label = this.$t(
                        `observation_fe['filter.groupBy.${this.widgetSettings?.groupBy}.default']`
                    ).toString()
                }
                break
            case 'reporter':
            case 'responsibleLeader':
            case 'responsibleUser':
                if (item.groupResource?.data?.name) {
                    id = item.groupResource.data.id
                    label = `${item.groupResource.data.name}`
                    color = getColorForValue(
                        `${item.groupResource.data?.id}${item.groupResource.data.name}`
                    )
                } else {
                    if (groupBy == 'reporter') {
                        label = this.$t(`observation_fe['observation.reporter.null']`).toString()
                    } else {
                        label = this.$t(
                            `observation_fe['filter.groupBy.${this.widgetSettings?.groupBy}.default']`
                        ).toString()
                    }
                }
                break

            case 'reporterCompany':
                if (item.groupResource?.data?.name) {
                    id = item.groupResource.data.id
                    label = `${item.groupResource.data.name}`
                    color = getColorForValue(
                        `${item.groupResource.data?.id}${item.groupResource.data.name}`
                    )
                } else {
                    label = this.$t(
                        `observation_fe['filter.groupBy.${this.widgetSettings?.groupBy}.default']`
                    ).toString()
                }
                break

            case 'contractors':
                if (item.groupResource?.data?.id) {
                    id = item.groupResource.data.id
                    label =
                        item.groupResource.data.namePath[
                            item.groupResource.data.namePath.length - 1
                        ]
                    color = getColorForValue(
                        `${item.groupResource.data?.id}${item.groupResource.data.namePath.join('')}`
                    )
                } else {
                    label = this.$t(
                        `observation_fe['filter.groupBy.${groupBy}.default']`
                    ).toString()
                }
                break

            case 'project':
            case 'relatedCompanies':
                if (item.groupResource?.data?.id) {
                    id = item.groupResource.data.id
                    label = item.groupResource.data.name
                    color = getColorForValue(
                        `${item.groupResource.data?.id}${item.groupResource.data.name}`
                    )
                } else {
                    label = this.$t(
                        `observation_fe['filter.groupBy.${groupBy}.default']`
                    ).toString()
                }
                break

            case 'location':
            case 'category':
            case 'cause':
                if (item.groupResource?.data?.name) {
                    id = item.groupResource.data.id
                    label = `${item.groupResource.data.name}`
                    color = getColorForValue(
                        `${item.groupResource.data?.id}${item.groupResource.data.name}`
                    )
                } else {
                    label = this.$t(
                        `observation_fe['filter.groupBy.${this.widgetSettings?.groupBy}.default']`
                    ).toString()
                }
                break

            case 'locationName':
            case 'categoryName':
            case 'causeName':
                if (item.groupValue) {
                    id = item.groupValue
                    label = `${item.groupValue}`
                    color = getColorForValue(`${item.groupValue}`)
                } else {
                    label = this.$t(
                        `observation_fe['filter.groupBy.${this.widgetSettings?.groupBy}.default']`
                    ).toString()
                }
                break
        }

        return {
            label: label,
            borderColor: color,
            backgroundColor: color,
            id: id,
        }
    }

    elClicked(labelIndex: number, dataIndex: number | null = null) {
        // labelIndex - index for groupBy
        // groupIndex - index for aggregatePeriod
        if (!this.widgetSettings || isNaN(labelIndex)) return

        // BE doesn't have filters in observation search for this
        if (
            this.widgetSettings?.groupBy == 'reporterRole' ||
            this.widgetSettings?.groupBy == 'reporterCompanyRole'
        )
            return

        // Starting generate filters for observations
        const filters = {
            ...setObservationFilter(this.widgetSettings),
            ...(this.widgetSettings?.groupBy &&
                this.datasets[labelIndex].id && {
                    [this.widgetSettings?.groupBy]: this.datasets[labelIndex].id,
                }),
        }

        //Set observations filters for aggregated period
        if (this.widgetSettings.aggregatePeriod && dataIndex != null) {
            const period = this.datasets[labelIndex]?.data[dataIndex]?.p
            if (period) {
                switch (this.widgetSettings.aggregatePeriodSource) {
                    case 'happenedAt':
                        filters.happenedAt = {
                            ge: period.ge,
                            le: period.le,
                        }
                        break
                    case 'publishedAt':
                        filters.publishedAt = {
                            ge: period.ge,
                            le: period.le,
                        }
                        break
                    case 'updatedAt':
                        filters.updatedAt = {
                            ge: period.ge,
                            le: period.le,
                        }
                        break
                    case 'closedAt':
                        filters.closedAt = {
                            ge: period.ge,
                            le: period.le,
                        }
                        break
                }
            }
        }

        //Set observation filters for grouppedBy
        if (this.widgetSettings?.groupBy) {
            if (this.datasets[labelIndex].id) {
                filters[this.widgetSettings?.groupBy] = this.datasets[labelIndex].id
            } else {
                if (this.widgetSettings?.groupBy == 'reporter') {
                    filters[`${this.widgetSettings?.groupBy}IsNull`] = true
                } else {
                    filters[this.widgetSettings?.groupBy] = ['null']
                }
            }
        }

        this.$store.commit('customFilters', filters)

        this.$router.push({
            name: 'observations',
        })
    }

    // Filters dynamic
    filterLabel(key: any) {
        switch (key) {
            case 'categoryName':
                key = 'category'
                break
            case 'causeName':
                key = 'cause'
                break
            case 'locationName':
                key = 'location'
                break
        }

        const labelTranslation = `observation_labels['observation.${key}']`
        const filterTranslation = `observation_fe['filter.${key}.label']`

        return this.$te(labelTranslation)
            ? this.$t(labelTranslation)
            : this.$te(filterTranslation)
            ? this.$t(filterTranslation)
            : this.$t(`observation_fe['filter.${key}.label']`)
    }

    allFilters = [
        ...observationStatisticsSingleValues,
        ...observationStatisticsArrayValues,
        ...observationStatisticsObjectValues,
    ]

    get selectableFilters() {
        let arr = this.allFilters

        arr = arr.filter(
            (filter) =>
                filter != 'aggregateFunction' &&
                filter != 'aggregateField' &&
                filter != 'aggregatePeriodSource' &&
                filter != 'projectGroup'
        )

        if (this.$route.params.type == 'p') {
            arr = arr.filter((filter) => filter != 'project')
        }

        if (this.project) {
            arr = arr.filter(
                (filter) =>
                    filter != 'relatedCompany' &&
                    filter != 'categoryName' &&
                    filter != 'causeName' &&
                    filter != 'locationName'
            )
        } else if (this.group) {
            arr = arr.filter(
                (filter) =>
                    filter != 'contractors' &&
                    filter != 'category' &&
                    filter != 'cause' &&
                    filter != 'location'
            )
        }
        return arr
    }

    get availableFilters() {
        return sortFilters(this.lodash.difference(this.selectableFilters, this.addedFilters)).map(
            (item) => {
                return {
                    value: item,
                    text: this.filterLabel(item),
                }
            }
        )
    }

    addedFilters = [] as Array<string>

    addFilter() {
        if (this.availableFilters[0]) {
            this.initFilterValue(this.availableFilters[0].value)
            this.addedFilters.push(this.availableFilters[0].value)
        }
    }

    deleteFilter(index: number) {
        this.initFilterValue(this.addedFilters[index])
        this.addedFilters.splice(index, 1)
    }

    setFilterName(index: number, val: string) {
        this.initFilterValue(this.addedFilters[index])
        this.addedFilters.splice(index, 1, val)
    }

    initFilterValue(filter: string) {
        if (!this.widgetSettings) return

        if (observationStatisticsSingleValues.includes(filter)) {
            this.widgetSettings[filter] = null
        } else if (observationStatisticsArrayValues.includes(filter)) {
            this.widgetSettings[filter] = []
        } else if (observationStatisticsObjectValues.includes(filter)) {
            this.widgetSettings[filter] = {
                ge: null,
                le: null,
                period: null,
            }
        }
    }

    handleFilters(setAdded = false) {
        if (!this.widgetSettings) return

        if (setAdded) this.addedFilters = []

        Object.keys(this.widgetSettings).map((filter) => {
            const isAvalableDynamicFilter =
                this.availableFilters.findIndex((val) => val.value == filter) > -1
            if (!isAvalableDynamicFilter) return

            if (observationStatisticsSingleValues.includes(filter)) {
                if (this.widgetSettings && this.widgetSettings[filter] != null) {
                    if (setAdded) this.addedFilters.push(filter)
                }
            } else if (observationStatisticsArrayValues.includes(filter)) {
                if (
                    this.widgetSettings &&
                    this.widgetSettings[filter] &&
                    this.widgetSettings[filter].length != 0
                ) {
                    if (setAdded) this.addedFilters.push(filter)
                }
            } else if (observationStatisticsObjectValues.includes(filter)) {
                if (
                    this.widgetSettings &&
                    (this.widgetSettings[filter]['ge'] != null ||
                        this.widgetSettings[filter]['le'] != null ||
                        this.widgetSettings[filter]['period'] != null)
                ) {
                    if (setAdded) this.addedFilters.push(filter)
                }
            }
        })
    }

    async toggleDialog(val) {
        this.setValues()
        this.handleFilters(val)
    }
}
